/**
 * PERSON LABEL — the ONE rule for showing a person: their REAL full NAME, else their EMAIL, else "Unknown member". A
 * "name" that is just the email's local part re-cased ("Amarpani" for amarpani@…) is NOT a name (L152). Never a raw
 * identifier (a Cognito sub / UUID / ARN) as visible text — the id may only appear as a hover ``title``.
 * Accepts any of the shapes the API returns (userName | displayName | name, email | userEmail).
 * TRAP: a ROOM-roster row's ``displayName`` is the ROOM's name, not the person's — pass such rows' person fields only
 * (``{userName, email}``; see PoolSettingsDrawer ``rosterPerson``).
 */
const UUIDISH = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Is this string an identifier rather than something a person would recognise? */
export function looksLikeId(v) {
  const s = String(v || '').trim();
  return !!s && (UUIDISH.test(s) || s.startsWith('arn:') || /^(USER|TENANT|ORG)#/.test(s));
}

/** The name someone would derive from an email's local part ("amarpani.x@…" → "amarpani x"), lower-case. */
export function nameFromEmail(email) {
  const e = String(email || '').trim();
  if (!e.includes('@')) return '';
  return e.split('@')[0].replace(/[._-]+/g, ' ').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function personName(p) {
  if (!p) return '';
  const email = personEmail(p);
  for (const k of ['userName', 'displayName', 'name', 'fullName']) {
    const v = String(p[k] || '').trim().replace(/\s+/g, ' ');
    if (!v || looksLikeId(v) || v.includes('@')) continue;
    if (email && v.toLowerCase() === nameFromEmail(email)) continue;   // derived from the email — not a real name
    return v;
  }
  return '';
}

export function personEmail(p) {
  if (!p) return '';
  for (const k of ['email', 'userEmail', 'targetEmail', 'userName', 'displayName', 'name', 'fullName']) {   // a name field holding an email IS the email
    const v = String(p[k] || '').trim();
    if (v && v.includes('@')) return v;
  }
  return '';
}

/** Primary visible text for a person. */
export function personLabel(p, fallback = 'Unknown member') {
  return personName(p) || personEmail(p) || fallback;
}

/** Secondary line: the email when the primary is the name (else nothing). */
export function personSecondary(p) {
  const n = personName(p);
  const e = personEmail(p);
  return n && e ? e : '';
}
